export type ControlSlideLatestWinsParams = {
  mediaId: string;
  entityType: string;
  entityId: string;
  action: 'next' | 'prev' | 'first' | 'last' | 'goto';
  slideNumber?: number;
};

type DrainSlot<T> = {
  queue: ControlSlideLatestWinsParams[];
  run?: Promise<T>;
  lastResult?: T;
};

const drains = new Map<string, DrainSlot<unknown>>();

function isNativeSlideStep(action: ControlSlideLatestWinsParams['action']): boolean {
  return action === 'next' || action === 'prev';
}

export function controlSlideDeckKey(
  params: Pick<ControlSlideLatestWinsParams, 'entityType' | 'entityId' | 'mediaId'>,
): string {
  return `${params.entityType}:${params.entityId}:${params.mediaId}`;
}

/**
 * One in-flight control-slide HTTP per deck.
 * Native next/prev enqueue FIFO so click-builds are not dropped.
 * Goto/first/last replace the queue so a jump drops leftover steps.
 */
export async function enqueueControlSlideLatestWins<T>(
  params: ControlSlideLatestWinsParams,
  send: (next: ControlSlideLatestWinsParams) => Promise<T>,
): Promise<T> {
  const key = controlSlideDeckKey(params);
  let slot = drains.get(key) as DrainSlot<T> | undefined;
  if (!slot) {
    slot = { queue: [] };
    drains.set(key, slot as DrainSlot<unknown>);
  }
  if (isNativeSlideStep(params.action)) {
    slot.queue.push(params);
  } else {
    slot.queue = [params];
  }

  if (!slot.run) {
    slot.run = (async () => {
      try {
        while (slot.queue.length > 0) {
          const next = slot.queue.shift();
          if (!next) {
            break;
          }
          slot.lastResult = await send(next);
        }
        return slot.lastResult as T;
      } finally {
        drains.delete(key);
      }
    })();
  }

  return slot.run;
}

/** Test-only: drop in-flight coalescers so specs do not leak across cases. */
export function resetControlSlideLatestWinsForTests(): void {
  drains.clear();
}
